/**
 * DigestHeader — "This week's reading"
 *
 * The editorial headline above the trends dashboard: one sentence and a row
 * of flag chips distilled from the patient's analytics (significant trends,
 * early-warning signals, sudden assessment shifts). Every claim shown here
 * survived FDR correction or the Tang-DeRubeis criteria — the templates are
 * deterministic, no LLM involved.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../config";
import { fetchWithRetry } from "../lib/fetchRetry";
import { useAuth } from "../auth/AuthProvider";
import {
  computeTopicTrend,
  computeEarlyWarning,
  computeAssessmentSeries,
} from "../lib/chartSpecs";
import type { ExtractorRow } from "../lib/chartSpecs";
import { buildDigest } from "../lib/digest";
import type { DigestChip } from "../lib/digest";
import type { ChartHint, SessionMetricsRow } from "../types/charts";
import "./DigestHeader.css";

interface ServerPrompt {
  id: string;
  title: string;
  kind: string;
}

type Props = {
  patient: string;
  patientName?: string;
  prompts: ServerPrompt[];
};

const VALID_HINTS: ChartHint[] = ["simple", "stacked", "multi", "airtime", "assessment", "markers"];

const CHIP_GLYPH: Record<DigestChip["kind"], string> = {
  up: "↑",
  down: "↓",
  warning: "⚠",
  gain: "✦",
  loss: "✦",
};

/** Wrap the interpolated parameter values in <em> for the editorial accent. */
function renderHeadline(text: string, params: Record<string, string>): React.ReactNode {
  const values = Object.values(params).filter(Boolean);
  if (values.length === 0) return text;
  const pattern = values
    .map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const parts = text.split(new RegExp(`(${pattern})`, "g"));
  return parts.map((part, i) =>
    values.includes(part) ? <em key={i}>{part}</em> : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

const DigestHeader: React.FC<Props> = ({ patient, patientName, prompts }) => {
  const { t } = useTranslation();
  const { ensureToken } = useAuth();

  const [rows, setRows] = useState<ExtractorRow[]>([]);
  const [sessions, setSessions] = useState<SessionMetricsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!patient || prompts.length === 0) return;
    const key = `${patient}|${prompts.map((p) => p.id).join(",")}`;
    if (requestedRef.current === key) return;
    requestedRef.current = key;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await ensureToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        const rowFetches = prompts.map(async (p) => {
          const url = `${API_BASE}/tags/topic_distribution?patient_id=${encodeURIComponent(patient)}&prompt_pks=${encodeURIComponent(p.id)}`;
          const r = await fetchWithRetry(url, { headers });
          if (!r.ok) throw new Error(`HTTP ${r.status} (${p.title})`);
          const res = await r.json();
          const hint: ChartHint = VALID_HINTS.includes(res?.chart_hint) ? res.chart_hint : "stacked";
          const items = Array.isArray(res?.items) ? res.items : [];
          const out: ExtractorRow[] = [];
          for (const it of items) {
            const date = String(it?.date ?? "");
            const topic = String(it?.topic ?? "");
            const percent = Number(it?.percent);
            if (date && topic && Number.isFinite(percent)) {
              out.push({
                date,
                session_id: String(it?.session_id ?? ""),
                topic,
                percent,
                extractor: p.title || p.id,
                extractorHint: hint,
              });
            }
          }
          return out;
        });

        const metricsFetch = (async () => {
          const r = await fetchWithRetry(
            `${API_BASE}/tags/session_metrics?patient_id=${encodeURIComponent(patient)}`,
            { headers }
          );
          if (!r.ok) throw new Error(`HTTP ${r.status} (session metrics)`);
          const res = await r.json();
          return (Array.isArray(res?.items) ? res.items : []) as SessionMetricsRow[];
        })();

        const [rowGroups, metrics] = await Promise.all([
          Promise.all(rowFetches),
          metricsFetch,
        ]);
        if (cancelled) return;
        setRows(rowGroups.flat());
        setSessions(metrics);
      } catch (e) {
        if (cancelled) return;
        console.error("[DigestHeader] load failed:", e);
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patient, prompts, ensureToken]);

  const digest = useMemo(() => {
    const trends = computeTopicTrend(rows).results;
    const warnings = computeEarlyWarning(rows).results;
    const assessments = computeAssessmentSeries(rows).results;
    return buildDigest(trends, warnings, assessments, rows.length > 0);
  }, [rows]);

  const meta = useMemo(() => {
    if (sessions.length === 0) return null;
    const dates = sessions.map((s) => s.date).filter(Boolean).sort();
    const last = dates[dates.length - 1];
    if (!last) return null;
    const days = Math.max(
      0,
      Math.floor((Date.now() - new Date(last + "T00:00:00").getTime()) / 86400_000)
    );
    const when =
      days === 0 ? t("digest.today") : t("digest.daysAgo", { count: days });
    return t("digest.sessionsMeta", { count: sessions.length, when });
  }, [sessions, t]);

  if (!patient || prompts.length === 0) return null;

  const headlineText = t(`digest.${digest.headline.key}`, digest.headline.params);

  return (
    <div className="digest">
      <div className="digest-eyebrow">{t("digest.eyebrow")}</div>
      {loading ? (
        <div className="digest-loading">{t("digest.loading")}</div>
      ) : error ? (
        <div className="digest-error">{t("digest.error")}: {error}</div>
      ) : (
        <>
          <h1 className="digest-h1">
            {renderHeadline(headlineText, digest.headline.params)}
          </h1>
          {meta && <div className="digest-sub">{meta}</div>}
          {digest.chips.length > 0 && (
            <div className="digest-chips">
              {digest.chips.map((c, i) => (
                <span key={i} className={`digest-chip digest-chip-${c.kind}`} title={c.detail}>
                  {c.label} {CHIP_GLYPH[c.kind]}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DigestHeader;
